import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Space,
  Switch,
  Typography,
  Upload,
} from 'antd';
import type { RcFile } from 'antd/es/upload';
import {
  DeleteOutlined,
  InboxOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';

import type {
  PartnerShopLookbookPhoto,
  PartnerShopLookbookSection,
} from '../PartnerCreatePage';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface LookbookStepProps {
  partnerShopLookbookSections: PartnerShopLookbookSection[];
  onChangePartnerShopLookbookSections: (
    nextPartnerShopLookbookSections: PartnerShopLookbookSection[]
  ) => void;
}

function getNextSortOrder<T extends { sortOrder: number }>(items: T[]) {
  if (!items || items.length === 0) return 1;

  return Math.max(...items.map((item) => Number(item.sortOrder || 0))) + 1;
}

function createClientKey() {
  return `lookbook_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function revokePreviewUrl(partnerShopLookbookPhoto?: PartnerShopLookbookPhoto) {
  if (
    partnerShopLookbookPhoto?.previewUrl &&
    partnerShopLookbookPhoto.previewUrl.startsWith('blob:')
  ) {
    URL.revokeObjectURL(partnerShopLookbookPhoto.previewUrl);
  }
}

function revokeSectionPreviewUrls(
  partnerShopLookbookSection?: PartnerShopLookbookSection
) {
  if (!partnerShopLookbookSection) return;

  partnerShopLookbookSection.photos.forEach((partnerShopLookbookPhoto) => {
    revokePreviewUrl(partnerShopLookbookPhoto);
  });
}

function getVisibleSections(
  partnerShopLookbookSections: PartnerShopLookbookSection[]
) {
  return Array.isArray(partnerShopLookbookSections)
    ? partnerShopLookbookSections
    : [];
}

export default function LookbookStep({
  partnerShopLookbookSections,
  onChangePartnerShopLookbookSections,
}: LookbookStepProps) {
  const visibleSections = getVisibleSections(partnerShopLookbookSections);

  const totalPhotoCount = useMemo(() => {
    return visibleSections.reduce((sum, partnerShopLookbookSection) => {
      return sum + partnerShopLookbookSection.photos.length;
    }, 0);
  }, [visibleSections]);

  useEffect(() => {
    return () => {
      visibleSections.forEach((partnerShopLookbookSection) => {
        revokeSectionPreviewUrls(partnerShopLookbookSection);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePartnerShopLookbookSections = (
    nextPartnerShopLookbookSections: PartnerShopLookbookSection[]
  ) => {
    onChangePartnerShopLookbookSections(nextPartnerShopLookbookSections);
  };

  const addPartnerShopLookbookSection = () => {
    updatePartnerShopLookbookSections([
      ...visibleSections,
      {
        sectionName: '',
        description: '',
        isPublished: 1,
        sortOrder: getNextSortOrder(visibleSections),
        delYn: 0,
        photos: [],
      },
    ]);
  };

  const updatePartnerShopLookbookSection = (
    sectionIndex: number,
    patch: Partial<PartnerShopLookbookSection>
  ) => {
    updatePartnerShopLookbookSections(
      visibleSections.map((partnerShopLookbookSection, index) => {
        if (index !== sectionIndex) {
          return partnerShopLookbookSection;
        }

        return {
          ...partnerShopLookbookSection,
          ...patch,
        };
      })
    );
  };

  const removePartnerShopLookbookSection = (sectionIndex: number) => {
    const targetPartnerShopLookbookSection = visibleSections[sectionIndex];

    revokeSectionPreviewUrls(targetPartnerShopLookbookSection);

    updatePartnerShopLookbookSections(
      visibleSections.filter((_, index) => index !== sectionIndex)
    );
  };

  const addFilesToPartnerShopLookbookSection = (
    sectionIndex: number,
    files: RcFile[]
  ) => {
    const partnerShopLookbookSection = visibleSections[sectionIndex];

    if (!partnerShopLookbookSection) return;

    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length === 0) return;

    const currentPhotos = partnerShopLookbookSection.photos || [];
    const currentMaxSortOrder = getNextSortOrder(currentPhotos) - 1;
    const hasRepresentative = currentPhotos.some(
      (partnerShopLookbookPhoto) =>
        Number(partnerShopLookbookPhoto.isRepresentative) === 1
    );

    const nextPhotos: PartnerShopLookbookPhoto[] = imageFiles.map(
      (file, fileIndex) => ({
        clientKey: createClientKey(),
        lookbookSectionId: partnerShopLookbookSection.id ?? null,

        file,
        previewUrl: URL.createObjectURL(file),
        originalFileName: file.name,

        category: partnerShopLookbookSection.sectionName || '',
        title: '',
        description: '',

        isRepresentative: !hasRepresentative && fileIndex === 0 ? 1 : 0,
        isPublished: 1,
        sortOrder: currentMaxSortOrder + fileIndex + 1,
        delYn: 0,
      })
    );

    updatePartnerShopLookbookSection(sectionIndex, {
      photos: [...currentPhotos, ...nextPhotos],
    });
  };

  const updatePartnerShopLookbookPhoto = (
    sectionIndex: number,
    photoIndex: number,
    patch: Partial<PartnerShopLookbookPhoto>
  ) => {
    const partnerShopLookbookSection = visibleSections[sectionIndex];

    if (!partnerShopLookbookSection) return;

    updatePartnerShopLookbookSection(sectionIndex, {
      photos: partnerShopLookbookSection.photos.map(
        (partnerShopLookbookPhoto, index) => {
          if (index !== photoIndex) {
            return partnerShopLookbookPhoto;
          }

          return {
            ...partnerShopLookbookPhoto,
            ...patch,
          };
        }
      ),
    });
  };

  const removePartnerShopLookbookPhoto = (
    sectionIndex: number,
    photoIndex: number
  ) => {
    const partnerShopLookbookSection = visibleSections[sectionIndex];

    if (!partnerShopLookbookSection) return;

    const targetPartnerShopLookbookPhoto =
      partnerShopLookbookSection.photos[photoIndex];

    revokePreviewUrl(targetPartnerShopLookbookPhoto);

    const nextPhotos = partnerShopLookbookSection.photos.filter(
      (_, index) => index !== photoIndex
    );

    if (
      nextPhotos.length > 0 &&
      !nextPhotos.some(
        (partnerShopLookbookPhoto) =>
          Number(partnerShopLookbookPhoto.isRepresentative) === 1
      )
    ) {
      nextPhotos[0] = {
        ...nextPhotos[0],
        isRepresentative: 1,
      };
    }

    updatePartnerShopLookbookSection(sectionIndex, {
      photos: nextPhotos,
    });
  };

  const setRepresentativePartnerShopLookbookPhoto = (
    sectionIndex: number,
    photoIndex: number
  ) => {
    const partnerShopLookbookSection = visibleSections[sectionIndex];

    if (!partnerShopLookbookSection) return;

    updatePartnerShopLookbookSection(sectionIndex, {
      photos: partnerShopLookbookSection.photos.map(
        (partnerShopLookbookPhoto, index) => ({
          ...partnerShopLookbookPhoto,
          isRepresentative: index === photoIndex ? 1 : 0,
        })
      ),
    });
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              룩북 사진 구성
            </Title>
            <Text type="secondary">
              업체가 고객에게 보여주고 싶은 공식 사진을 그룹별로 정리합니다.
              사진은 최종 생성 시 서버에서 업로드하고, 필요한 크기로 변환합니다.
            </Text>
          </div>

          <Space size={16}>
            <Text strong>사진 그룹 {visibleSections.length}개</Text>
            <Text strong>사진 {totalPhotoCount}장</Text>
          </Space>
        </Space>
      </Card>

      {visibleSections.length === 0 && (
        <Card>
          <Empty description="아직 추가된 사진 그룹이 없습니다." />
        </Card>
      )}

      {visibleSections.map((partnerShopLookbookSection, sectionIndex) => (
        <Card
          key={`${partnerShopLookbookSection.id || 'new'}-${sectionIndex}`}
          bodyStyle={{ padding: 20 }}
          title={
            <Space>
              <Text strong>사진 그룹 {sectionIndex + 1}</Text>
              <Text type="secondary">
                사진 {partnerShopLookbookSection.photos.length}장
              </Text>
            </Space>
          }
          extra={
            <Space>
              <Text type="secondary">노출</Text>
              <Switch
                checked={Number(partnerShopLookbookSection.isPublished) === 1}
                onChange={(checked) =>
                  updatePartnerShopLookbookSection(sectionIndex, {
                    isPublished: checked ? 1 : 0,
                  })
                }
              />

              <Popconfirm
                title="이 사진 그룹을 삭제할까요?"
                description="그룹 안의 사진도 함께 제거됩니다."
                okText="삭제"
                cancelText="취소"
                onConfirm={() =>
                  removePartnerShopLookbookSection(sectionIndex)
                }
              >
                <Button danger icon={<DeleteOutlined />}>
                  그룹 삭제
                </Button>
              </Popconfirm>
            </Space>
          }
        >
          <Space direction="vertical" size={18} style={{ width: '100%' }}>
            <Row gutter={12}>
              <Col xs={24} md={10}>
                <Text strong>그룹명</Text>
                <Input
                  value={partnerShopLookbookSection.sectionName}
                  placeholder="예: 맞춤 예복"
                  style={{ marginTop: 6 }}
                  onChange={(event) =>
                    updatePartnerShopLookbookSection(sectionIndex, {
                      sectionName: event.target.value,
                      photos: partnerShopLookbookSection.photos.map(
                        (partnerShopLookbookPhoto) => ({
                          ...partnerShopLookbookPhoto,
                          category:
                            partnerShopLookbookPhoto.category ||
                            event.target.value,
                        })
                      ),
                    })
                  }
                />
              </Col>

              <Col xs={24} md={4}>
                <Text strong>정렬 순서</Text>
                <InputNumber
                  value={partnerShopLookbookSection.sortOrder}
                  min={0}
                  style={{ width: '100%', marginTop: 6 }}
                  onChange={(nextValue) =>
                    updatePartnerShopLookbookSection(sectionIndex, {
                      sortOrder: Number(nextValue || 0),
                    })
                  }
                />
              </Col>

              <Col xs={24} md={10}>
                <Text strong>그룹 설명</Text>
                <TextArea
                  value={partnerShopLookbookSection.description}
                  rows={2}
                  placeholder="이 그룹에서 보여줄 사진의 분위기나 설명을 입력해 주세요."
                  style={{ marginTop: 6 }}
                  onChange={(event) =>
                    updatePartnerShopLookbookSection(sectionIndex, {
                      description: event.target.value,
                    })
                  }
                />
              </Col>
            </Row>

            <Dragger
              accept="image/*"
              multiple
              showUploadList={false}
              beforeUpload={(file, fileList) => {
                const firstFile = fileList[0] as RcFile;

                if ((file as RcFile).uid === firstFile.uid) {
                  addFilesToPartnerShopLookbookSection(
                    sectionIndex,
                    fileList as RcFile[]
                  );
                }

                return false;
              }}
              style={{
                background: '#fafafa',
                borderRadius: 10,
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                사진을 한 번에 여러 장 선택하거나 여기에 끌어다 놓으세요.
              </p>
              <p className="ant-upload-hint">
                원본 사진은 브라우저에만 보관되고, 최종 생성 시 서버로 전송됩니다.
              </p>
            </Dragger>

            {partnerShopLookbookSection.photos.length === 0 ? (
              <Card size="small">
                <Empty description="아직 선택된 사진이 없습니다." />
              </Card>
            ) : (
              <Row gutter={[16, 16]}>
                {partnerShopLookbookSection.photos.map(
                  (partnerShopLookbookPhoto, photoIndex) => {
                    const imageSrc = partnerShopLookbookPhoto.previewUrl || '';

                    return (
                      <Col
                        key={`${partnerShopLookbookPhoto.clientKey}-${photoIndex}`}
                        xs={24}
                        md={12}
                        xl={8}
                      >
                        <Card
                          size="small"
                          bodyStyle={{ padding: 12 }}
                          title={
                            <Space>
                              <Text strong>사진 {photoIndex + 1}</Text>
                              {partnerShopLookbookPhoto.originalFileName && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {partnerShopLookbookPhoto.originalFileName}
                                </Text>
                              )}
                            </Space>
                          }
                          extra={
                            <Space>
                              <Button
                                size="small"
                                type={
                                  Number(
                                    partnerShopLookbookPhoto.isRepresentative
                                  ) === 1
                                    ? 'primary'
                                    : 'default'
                                }
                                icon={
                                  Number(
                                    partnerShopLookbookPhoto.isRepresentative
                                  ) === 1 ? (
                                    <StarFilled />
                                  ) : (
                                    <StarOutlined />
                                  )
                                }
                                onClick={() =>
                                  setRepresentativePartnerShopLookbookPhoto(
                                    sectionIndex,
                                    photoIndex
                                  )
                                }
                              >
                                대표
                              </Button>

                              <Switch
                                size="small"
                                checked={
                                  Number(
                                    partnerShopLookbookPhoto.isPublished
                                  ) === 1
                                }
                                onChange={(checked) =>
                                  updatePartnerShopLookbookPhoto(
                                    sectionIndex,
                                    photoIndex,
                                    {
                                      isPublished: checked ? 1 : 0,
                                    }
                                  )
                                }
                              />

                              <Popconfirm
                                title="이 사진을 삭제할까요?"
                                okText="삭제"
                                cancelText="취소"
                                onConfirm={() =>
                                  removePartnerShopLookbookPhoto(
                                    sectionIndex,
                                    photoIndex
                                  )
                                }
                              >
                                <Button
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                />
                              </Popconfirm>
                            </Space>
                          }
                        >
                          <Space
                            direction="vertical"
                            size={10}
                            style={{ width: '100%' }}
                          >
                            <div
                              style={{
                                width: '100%',
                                aspectRatio: '4 / 3',
                                background: '#f5f5f5',
                                borderRadius: 8,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {imageSrc ? (
                                <Image
                                  src={imageSrc}
                                  alt={
                                    partnerShopLookbookPhoto.title ||
                                    '룩북 사진'
                                  }
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <Text type="secondary">사진 없음</Text>
                              )}
                            </div>

                            <Row gutter={8}>
                              <Col span={16}>
                                <Text strong>사진 제목</Text>
                                <Input
                                  value={partnerShopLookbookPhoto.title || ''}
                                  placeholder="예: 네이비 맞춤 예복"
                                  style={{ marginTop: 6 }}
                                  onChange={(event) =>
                                    updatePartnerShopLookbookPhoto(
                                      sectionIndex,
                                      photoIndex,
                                      {
                                        title: event.target.value,
                                      }
                                    )
                                  }
                                />
                              </Col>

                              <Col span={8}>
                                <Text strong>정렬</Text>
                                <InputNumber
                                  value={partnerShopLookbookPhoto.sortOrder}
                                  min={0}
                                  style={{ width: '100%', marginTop: 6 }}
                                  onChange={(nextValue) =>
                                    updatePartnerShopLookbookPhoto(
                                      sectionIndex,
                                      photoIndex,
                                      {
                                        sortOrder: Number(nextValue || 0),
                                      }
                                    )
                                  }
                                />
                              </Col>
                            </Row>

                            <div>
                              <Text strong>사진 설명</Text>
                              <TextArea
                                value={
                                  partnerShopLookbookPhoto.description || ''
                                }
                                rows={3}
                                placeholder="사진에 대한 설명을 입력해 주세요."
                                style={{ marginTop: 6 }}
                                onChange={(event) =>
                                  updatePartnerShopLookbookPhoto(
                                    sectionIndex,
                                    photoIndex,
                                    {
                                      description: event.target.value,
                                    }
                                  )
                                }
                              />
                            </div>
                          </Space>
                        </Card>
                      </Col>
                    );
                  }
                )}
              </Row>
            )}
          </Space>
        </Card>
      ))}

      <Card
        style={{
          borderStyle: 'dashed',
          textAlign: 'center',
        }}
      >
        <Space direction="vertical" size={8}>
          <Text strong>새 사진 그룹을 추가할까요?</Text>
          <Text type="secondary">
            예: 맞춤 예복, 대여 예복, 매장 사진, 소품
          </Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addPartnerShopLookbookSection}
          >
            사진 그룹 추가
          </Button>
        </Space>
      </Card>
    </Space>
  );
}
